/**
 * Temporary teammates (settings wave §5.5, decisions 0025/0026): worker presets, the worker resolution used by
 * decide at RequestRun (and by the read tools and the web for the Assignee line), turn narrowing, and the ownership
 * facts ("owner left") that gate agents. Pure.
 */
import {
  DEFAULT_WORKER_PRESET,
  DEFAULT_WORKER_SETTINGS,
  narrowsAccess,
  type RoutingChoice,
  routeAgents,
} from "@minga/protocol"
import { CROSS_MODEL_WEIGHT } from "./placement"
import { type RoutingResolution, resolveRouting, routingChoicePlacement } from "./routing"
import type {
  AccessRequest,
  Agent,
  AgentKind,
  Hands,
  Item,
  ModelChoice,
  ModelsByAgent,
  Placement,
  Principal,
  ProjectState,
  Run,
  WorkerPreset,
  WorkerSettings,
} from "./types"

/** The project's worker settings; a project without `workers` uses the two built-in presets. */
export const workerSettingsOf = (s: ProjectState): WorkerSettings =>
  s.project?.workers ?? DEFAULT_WORKER_SETTINGS

/** A preset of the project by key. */
export const presetOf = (s: ProjectState, key: string): WorkerPreset | undefined =>
  workerSettingsOf(s).presets.find((p) => p.key === key)

/** The project's `default` preset (always present after K5; the built-in one defensively). */
export const defaultPresetOf = (s: ProjectState): WorkerPreset =>
  presetOf(s, "default") ?? DEFAULT_WORKER_PRESET

/** The worker preset an item's assignee names (null = the project's default; not a worker = none). */
export const presetKeyOf = (t: Pick<Item, "assignee">): string | null =>
  t.assignee?._tag === "worker" ? t.assignee.preset : null

export interface ResolvedWorker {
  /** The preset key the run records: the chosen key when it exists, else "default". */
  readonly presetKey: string
  readonly preset: WorkerPreset
  /** The run's frozen hands: the family pin with that family's model and effort (or the legacy family-less model). */
  readonly hands: Hands
  /** Model and effort per family, for a run whose family the lease decides. */
  readonly byAgent: {
    readonly claude: ModelChoice
    readonly codex: ModelChoice
    /** Decision 0109. */
    readonly antigravity: ModelChoice
    /** Decision 0110. */
    readonly muse: ModelChoice
  }
  /** "none": no repo default, no gh (K18), for the worker and its validators. */
  readonly repo: "project" | "none"
  /** The narrowing request, only when it narrows something. */
  readonly access: AccessRequest | undefined
  readonly routing: RoutingResolution
  readonly routeChoice: RoutingChoice | undefined
  readonly choicePlacement: Placement | null
}

const firstNonNull = <T>(...xs: ReadonlyArray<T | null | undefined>): T | null => {
  for (const x of xs) if (x !== null && x !== undefined) return x
  return null
}

/** A cross-model validator's family: Claude ↔ Codex; an Antigravity subject is checked on Claude (decision 0109). */
export const otherFamily = (k: AgentKind): AgentKind => (k === "claude" ? "codex" : "claude")

/** The CLI family a run ran on (its lease's profile, or a cloud mind's provider); before a lease, its pinned hands. */
export const runFamilyOf = (run: Pick<Run, "executor" | "hands">): AgentKind | null =>
  run.executor?._tag === "runner"
    ? run.executor.agent
    : run.executor?._tag === "platform"
      ? run.executor.provider === "anthropic"
        ? "claude"
        : "codex"
      : run.hands.agent

/**
 * Decision 0147 (lesson 4): what a cross-model validator adds to its placement. The other family is a strong
 * preference (above the asker's own runner and a preferred route), never a requirement: when no place offers it, the
 * validator runs on the author's family in a fresh session instead of waiting. The author's family stays eligible
 * even when it is an opt-in CLI (Antigravity, Muse) through a weight-0 entry.
 */
export const crossModelPlacement = (subjectKind: AgentKind): Placement => ({
  requires: {},
  prefers: [
    { match: { agent: [otherFamily(subjectKind)] }, weight: CROSS_MODEL_WEIGHT },
    { match: { agent: [subjectKind] }, weight: 0 },
  ],
  affinity: null,
  exclusive: [],
})

/**
 * Worker resolution (§5.5), deterministic in the state:
 * 1. preset: `presetKey` when given (a validator: the preset its subject ran with), else the item's
 *    `assignee.worker.preset`; null, absent or unknown = "default";
 * 2. family pin: item.hands.agent → the chosen preset's agent → the default preset's agent → owner.hands.agent;
 * 3. per family F: item.hands (when its agent is F) → chosen preset[F] → default preset[F] → owner.hands (when F);
 * 4. a validator with `crossModel` (decision 0147) pins no family: the lease decides (the other family preferred) and
 *    the hub takes that family's entry from `byAgent`;
 * 5. repo and 6. access from the chosen preset, else the default preset.
 */
export const resolveWorker = (
  s: ProjectState,
  item: Pick<Item, "assignee" | "hands" | "review"> & Partial<Pick<Item, "type" | "routing">>,
  owner: Pick<Agent, "hands"> | null,
  kind: "worker" | "validator" = "worker",
  subjectKind: AgentKind | null = null,
  presetKey: string | null = null,
): ResolvedWorker => {
  const chosenKey = presetKey ?? presetKeyOf(item)
  const chosen = chosenKey !== null ? presetOf(s, chosenKey) : undefined
  const def = defaultPresetOf(s)
  const preset = chosen ?? def
  const th = item.hands
  const policy = resolveRouting(s.project?.routing, { kind, itemType: item.type ?? null })
  const routeChoice = item.routing?.[kind]
  const choice = routeChoice ? routingChoicePlacement(routeChoice) : null
  // Required workflow placement dominates a discretionary task preference. Explicit task requirements are
  // combined by the normal placement resolver, which reports conflicting pins instead of weakening either.
  const routing = routeChoice
    ? {
        ...policy,
        reason: `${policy.reason}; task choice: ${routeChoice.reason}`,
      }
    : policy
  const ah = owner?.hands ?? { agent: null, model: null, effort: null }
  // Decision 0118: a required route allows one or several CLIs. One is the pin; with several, a lower pin (the item's
  // hands, the choice, a preset, the owner) holds only when every required route allows it, else nothing is pinned and
  // the placement's "any of" decides among the allowed CLIs.
  const ruleSet = routing.required ? routeAgents(routing.rule) : []
  const choiceSet = routeChoice?.mode === "required" ? routeAgents(routeChoice) : []
  const allowed = (a: AgentKind | null | undefined, set: ReadonlyArray<AgentKind>) =>
    a !== null && a !== undefined && (set.length === 0 || set.includes(a)) ? a : null
  const pin = firstNonNull<AgentKind>(
    ruleSet.length === 1 ? ruleSet[0] : null,
    allowed(th.agent, ruleSet),
    choiceSet.length === 1 ? allowed(choiceSet[0], ruleSet) : null,
    routeChoice ? null : allowed(chosen?.agent, ruleSet),
    routeChoice ? null : allowed(def.agent, ruleSet),
    routeChoice ? null : allowed(ah.agent, ruleSet),
  )
  const choiceFor = (f: AgentKind): ModelChoice => ({
    model: firstNonNull(
      routing.required ? routing.byAgent[f]?.model : null,
      th.agent === f ? th.model : null,
      routeChoice?.byAgent[f]?.model,
      chosen?.[f]?.model,
      routing.byAgent[f]?.model,
      def[f]?.model,
      ah.agent === f ? ah.model : null,
    ),
    effort: firstNonNull(
      routing.required ? routing.byAgent[f]?.effort : null,
      th.agent === f ? th.effort : null,
      routeChoice?.byAgent[f]?.effort,
      chosen?.[f]?.effort,
      routing.byAgent[f]?.effort,
      def[f]?.effort,
      ah.agent === f ? ah.effort : null,
    ),
  })
  const byAgent = {
    claude: choiceFor("claude"),
    codex: choiceFor("codex"),
    antigravity: choiceFor("antigravity"),
    muse: choiceFor("muse"),
  }
  // a family-less model from older events (kept as before this wave)
  const legacy: ModelChoice = {
    model: firstNonNull(th.agent === null ? th.model : null, ah.agent === null ? ah.model : null),
    effort: firstNonNull(th.agent === null ? th.effort : null, ah.agent === null ? ah.effort : null),
  }
  let hands: Hands = pin
    ? { agent: pin, model: byAgent[pin].model, effort: byAgent[pin].effort }
    : { agent: null, model: legacy.model, effort: legacy.effort }
  if (
    kind === "validator" &&
    item.review.crossModel &&
    subjectKind !== null &&
    !routing.required &&
    !routeChoice
  ) {
    // decision 0147: no pin; the placement prefers the other family and the hub picks `byAgent[family]` at Claim
    hands = { agent: null, model: null, effort: null }
  }
  const access = (chosen ?? def).access
  return {
    presetKey: preset.key,
    preset,
    hands,
    byAgent,
    repo: preset.repo,
    access: narrowsAccess(access) ? access : undefined,
    routing,
    routeChoice,
    choicePlacement: choice,
  }
}

/** `ModelsByAgent` as frozen on the run (both families; Antigravity only when something is chosen for it). */
export const byAgentOf = (r: ResolvedWorker): ModelsByAgent => ({
  claude: r.byAgent.claude,
  codex: r.byAgent.codex,
  ...(r.byAgent.antigravity.model !== null || r.byAgent.antigravity.effort !== null
    ? { antigravity: r.byAgent.antigravity }
    : {}),
  // decision 0110: only when it says something, so runs without Muse keep their stored shape
  ...(r.byAgent.muse.model !== null || r.byAgent.muse.effort !== null ? { muse: r.byAgent.muse } : {}),
})

/**
 * Persistent native turns inherit their configured profile. Shared requests retain the actual executor principal;
 * they do not silently narrow the agent to planner tools. Deliberate MCP/read-only restrictions remain effective.
 */
export const turnAccess = (
  agent: Pick<Agent, "mind"> & Partial<Pick<Agent, "ownerId" | "id">>,
  _askedBy?: Principal,
): AccessRequest | undefined => {
  if (agent.mind._tag !== "runner") return undefined
  return narrowsAccess(agent.mind.access) ? agent.mind.access : undefined
}

/** Native tools depend on the resolved placement, including an API agent explicitly routed to a runner. */
export const turnRunsNatively = (s: Pick<ProjectState, "project">, agent: Pick<Agent, "mind">): boolean => {
  const rule = resolveRouting(s.project?.routing, { kind: "turn" }).rule
  return agent.mind._tag === "runner" || rule?.runnerId !== undefined || rule?.profileId !== undefined
}

/** The agent's owner left the workspace (K10). Unknown before the first membership sync (no members yet). */
export const ownerLeft = (s: ProjectState, agent: Pick<Agent, "ownerId">): boolean =>
  s.members.length > 0 && !s.members.some((m) => m.userId === agent.ownerId)

/** The project is paused or archived: no new worker attempts (K8). */
export const projectPausesWork = (s: ProjectState): boolean =>
  s.project !== null && s.project.status !== "active"

/** The next W-n / V-n ref of the project (what decide mints at RequestRun). */
export const nextWorkerRef = (s: ProjectState, kind: "W" | "V"): string =>
  `${kind}-${(s.refCounters[kind] ?? 0) + 1}`
